'use client';

import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { BlockComponentModel } from '@/cms/types/blok-component';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/decodo/components/atoms/Text';
import { Accordion } from '@/decodo/components/molecules/accordion';
import {
  AccordionListComponent,
  DEFAULT_ACCORDION_TIMER_DURATION_SEC,
} from '@/decodo/components/molecules/accordion/AccordionList';
import { useAccordion } from '@/decodo/components/molecules/accordion/AccordionProvider';
import WithHasUserInteracted from '@/decodo/components/organisms/VideoAccordion/WithHasUserInteracted';

export interface ImageAccordionsStrapiProps {
  image: ImageStrapiProps;
  header?: BlockHeader;
  accordions: {
    title: string;
    description: RichTextFieldStrapiProps;
    button?: LinkButtonStrapiProps;
    image?: ImageStrapiProps;
  }[];
  backgroundVariant?: 'none' | 'grey' | 'gradient';
}

function ImageAccordionsStrapiComponent({
  block,
  ...rest
}: BlockComponentModel<ImageAccordionsStrapiProps>) {
  const { header, accordions, image: mainImage, backgroundVariant } = block;
  const { openItemId, setOpenItemId } = useAccordion();

  const handleAccordionClick = (id: string) => {
    setOpenItemId(id);
  };

  const imageToRender = accordions[Number(openItemId)]?.image ?? mainImage;

  const [displayedImage, setDisplayedImage] = useState(imageToRender);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    if (imageToRender !== displayedImage) {
      setIsFadingOut(true);

      const timeout = setTimeout(() => {
        setDisplayedImage(imageToRender);
        setIsFadingOut(false);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [imageToRender, displayedImage]);

  const backgroundVariantOptions =
    backgroundVariant === 'gradient'
      ? 'bg-main-light-gradient'
      : backgroundVariant === 'grey'
      ? 'bg-gray-100'
      : '';
  const formattedAccordionList =
    accordions?.map((item) => ({
      header: (
        <Text
          variant='subtitle-l-d'
          onClick={() => handleAccordionClick(item.toString())}
          classNames={{ wrapper: 'font-serif' }}
        >
          {item.title}
        </Text>
      ),
      children: (
        <div className='flex flex-col gap-6'>
          <div
            className={cn('rounded p-8 md:hidden', backgroundVariantOptions)}
          >
            <ImageStrapi
              key={openItemId}
              block={imageToRender}
              inner={{
                classNames: {
                  image: 'rounded',
                },
              }}
            />
          </div>
          <RichTextFieldStrapi block={item.description} />
          {item?.button && (
            <LinkButtonStrapi
              block={item.button}
              inner={{ classNames: { root: 'w-full md:w-fit' } }}
            />
          )}
        </div>
      ),
    })) || [];

  return (
    <BlockWrapper
      {...rest}
      data-tracker-section={SECTION_NAMES.IMAGE_ACCORDIONS}
    >
      <div className='relative flex w-full'>
        <div className='min-h-[560px] w-full md:mr-[2rem] md:w-1/2'>
          <BlockHeaderContent
            {...header}
            alignment='left'
            classNames={{ header: '!mb-12' }}
          >
            <WithHasUserInteracted>
              <AccordionListComponent
                accordionList={formattedAccordionList}
                internal={{
                  timer: {
                    durationSec: DEFAULT_ACCORDION_TIMER_DURATION_SEC,
                  },
                }}
              />
            </WithHasUserInteracted>
          </BlockHeaderContent>
        </div>
        <div
          className={cn(
            backgroundVariantOptions,
            'absolute right-0 top-0 h-full md:flex md:items-center md:justify-center md:rounded-l md:text-center 2xl:rounded',
            'left-1/2 -mt-20 ml-8 h-[720px] w-[calc(50vw-2.5rem)] max-w-[720px] overflow-hidden pb-20 pl-8 pt-20',
            'hidden'
          )}
        >
          <div
            className={cn(
              'relative w-full max-w-full overflow-hidden transition-opacity duration-300 ease-in-out',
              isFadingOut ? 'opacity-0' : 'opacity-100'
            )}
          >
            <ImageStrapi
              block={displayedImage}
              inner={{
                classNames: {
                  image: 'rounded max-w-[480px] w-full h-auto object-contain',
                },
              }}
            />
          </div>
        </div>
      </div>
    </BlockWrapper>
  );
}

export default function ImageAccordionsStrapi(
  props: BlockComponentModel<ImageAccordionsStrapiProps>
) {
  return (
    <Accordion>
      <ImageAccordionsStrapiComponent {...props} />
    </Accordion>
  );
}
